import { type FC, type ReactNode, useEffect, useState } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { Alert } from "../../common/Alert";

interface MarkdownArticleLayoutProps {
  sidebarTitle: string;
  sidebar: ReactNode;
  contentTitle: string;
  contentSubtitle?: string;
  loadingLabel?: string;
  markdownUrl: string | null;
  errorPrefix?: string;
}

export const MarkdownArticleLayout: FC<MarkdownArticleLayoutProps> = ({
  sidebarTitle,
  sidebar,
  contentTitle,
  contentSubtitle,
  loadingLabel = "Loading...",
  markdownUrl,
  errorPrefix = "Failed to load content",
}) => {
  const [markdown, setMarkdown] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!markdownUrl) {
      return;
    }

    setLoading(true);
    setError(null);

    fetch(markdownUrl)
      .then((res) => {
        if (!res.ok) {
          throw new Error("Not found");
        }
        return res.text();
      })
      .then((text) => {
        setMarkdown(text);
        setLoading(false);
      })
      .catch(() => {
        setError(errorPrefix);
        setLoading(false);
      });
  }, [markdownUrl, errorPrefix]);

  return (
    <div className="flex flex-col gap-5 lg:flex-row">
      <div className="glass-panel w-full flex-shrink-0 lg:w-72 lg:self-start">
        <div className="panel-header">
          <h2 className="font-mono text-[11px] font-bold uppercase tracking-widest text-gray-900 dark:text-white">
            {sidebarTitle}
          </h2>
        </div>
        <nav className="max-h-[70vh] overflow-y-auto p-2">{sidebar}</nav>
      </div>

      <div className="glass-panel min-w-0 flex-1 overflow-hidden">
        <div className="panel-header">
          <div className="flex items-center gap-3">
            <h2 className="font-mono text-[11px] font-bold uppercase tracking-widest text-gray-900 dark:text-white">
              {contentTitle}
            </h2>
            {contentSubtitle && (
              <span className="text-xs text-gray-400 dark:text-white/40">
                {contentSubtitle}
              </span>
            )}
          </div>
        </div>
        <div className="panel-body">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="font-mono text-[11px] font-bold uppercase tracking-widest text-gray-500 dark:text-white/60">
                {loadingLabel}
              </div>
            </div>
          )}

          {error && <Alert variant="error">{error}</Alert>}

          {!loading && !error && (
            <article className="markdown-content">
              <Markdown remarkPlugins={[remarkGfm]}>{markdown}</Markdown>
            </article>
          )}
        </div>
      </div>
    </div>
  );
};

interface SidebarItemProps {
  label: ReactNode;
  detail?: ReactNode;
  selected: boolean;
  onClick: () => void;
  indent?: boolean;
}

export const SidebarItem: FC<SidebarItemProps> = ({
  label,
  detail,
  selected,
  onClick,
  indent,
}) => (
  <button
    onClick={onClick}
    className={`flex w-full items-center justify-between rounded-none px-3 py-2 text-left text-sm transition-colors ${
      indent ? "pl-7" : ""
    } ${
      selected
        ? "bg-emerald-50 font-mono text-[11px] font-bold uppercase tracking-widest text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
        : "text-gray-600 hover:bg-gray-50 dark:text-white/60 dark:hover:bg-white/5"
    }`}
  >
    <span>{label}</span>
    {detail && (
      <span className="text-xs text-gray-400 dark:text-white/30">{detail}</span>
    )}
  </button>
);

interface SidebarGroupProps {
  label: string;
  children: ReactNode;
}

export const SidebarGroup: FC<SidebarGroupProps> = ({ label, children }) => (
  <div className="mb-3 last:mb-0">
    <p className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-white/40">
      {label}
    </p>
    {children}
  </div>
);
